import React, { useMemo } from "react"
import MyTooltip from "@common-utils/MyTooltip"
import { Form, Formik, FormikHelpers } from "formik"
import MyInputField from "@common-utils/fields/MyInputField"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { useConsultantsListingContext } from "./ConsultantsListing"
import moment from "moment/moment"
import { isEqual } from "lodash"

const ConsultantsListingSearchBar = () => {
     const globalContext = useConsultantsListingContext()

     // 1. Calculate Active Filters (Badge)
     const activeFilterCount = useMemo(() => {
          let count = 0
          const current = globalContext.filters

          // Check Keyword
          if (current.keyword && current.keyword.trim() !== "") count++

          // Check Date: Count ONLY if it is NOT today (the default)
          const today = moment().format("YYYY-MM-DD")
          if (current.dateFiltrage && current.dateFiltrage !== today) count++

          // Check Arrays (Clients, Responsables, Entites, Statuts, Contrats, Etats)
          if (current.clients && current.clients.length > 0) count++
          if (current.responsables && current.responsables.length > 0) count++
          if (current.entitesRattachement && current.entitesRattachement.length > 0) count++
          if (current.statuts && current.statuts.length > 0) count++
          if (current.contrats && current.contrats.length > 0) count++
          if (current.etatsConsultant && current.etatsConsultant.length > 0) count++

          return count
     }, [globalContext.filters])

     function handleSubmit(values: { keyword?: string }, helpers: FormikHelpers<{ keyword?: string }>) {
          const newFilters = { ...globalContext.filters, keyword: values.keyword }

          // Optimization: No change? Stop.
          if (isEqual(globalContext.filters, newFilters)) {
               helpers.setSubmitting(false)
               return
          }

          globalContext.setFilters(newFilters)
          globalContext.setItemInPreview(null)
     }

     function handleReset() {
          const defaultFilters = {
               page: 1,
               pageLength: 20,
               dateFiltrage: moment().format("YYYY-MM-DD"),
               keyword: "",
          }

          // Optimization: No change? Stop.
          if (isEqual(globalContext.filters, defaultFilters)) {
               globalContext.simpleSearchBarFormikRef.current?.resetForm({ values: { keyword: "" } })
               globalContext.filtersFormikRef.current?.resetForm({ values: defaultFilters })
               globalContext.simpleSearchBarFormikRef.current?.setSubmitting(false)
               return
          }

          globalContext.setFilters(defaultFilters)
          globalContext.simpleSearchBarFormikRef.current?.resetForm({ values: { keyword: "" } })
          globalContext.filtersFormikRef.current?.resetForm({ values: defaultFilters })

          globalContext.simpleSearchBarFormikRef.current?.setSubmitting(true)
          globalContext.setItemInPreview(null)
     }

     function handleClickShowFilters() {
          globalContext.setAreFiltersVisible(prev => !prev)
          globalContext.setItemInPreview(prev => (prev ? { ...prev, isPreviewVisible: false } : null))
     }

     return (
          <Formik<{ keyword?: string }>
               initialValues={{ keyword: globalContext.filters.keyword || "" }}
               onSubmit={handleSubmit}
               innerRef={globalContext.simpleSearchBarFormikRef}
               enableReinitialize
          >
               {helpers => {
                    return (
                         <Form noValidate autoComplete={"off"}>
                              <div className="input-group">
                                   <MyTooltip title={"Voir les filtres"}>
                                        <button type={"button"} className={"btn btn-sm btn-outline btn-outline-secondary position-relative"} onClick={handleClickShowFilters}>
                                             <i className={"fas fa-filter fs-4 text-dark"} />

                                             {/* 🔹 Active Filter Badge */}
                                             {activeFilterCount > 0 && (
                                                  <span
                                                       className="position-absolute top-0 start-0 translate-middle badge badge-circle badge-light-warning border border-warning d-flex align-items-center justify-content-center p-0"
                                                       style={{ width: "15px", height: "15px", fontSize: "9px" }}
                                                  >
                                                       {activeFilterCount}
                                                  </span>
                                             )}
                                        </button>
                                   </MyTooltip>

                                   {/* Bulk Selection Button */}
                                   {globalContext.listingQuery.data && globalContext.listingQuery.data.totalElementsInAllPages > 1 && (
                                        <MyTooltip
                                             title={globalContext.isBulkSelectionEnabled.enabled ? "Quitter le mode sélection multiple" : "Activer le mode sélection multiple"}
                                        >
                                             <button
                                                  type={"button"}
                                                  className={`btn btn-sm ${globalContext.isBulkSelectionEnabled.enabled ? "btn-secondary" : "btn-outline btn-outline-secondary"}`}
                                                  onClick={() =>
                                                       globalContext.setIsBulkSelectionEnabled(prev =>
                                                            prev.enabled
                                                                 ? {
                                                                        enabled: false,
                                                                        checkedElementsInPage: [],
                                                                        areAllElementsInAllPagesChecked: false,
                                                                   }
                                                                 : {
                                                                        ...prev,
                                                                        enabled: true,
                                                                   }
                                                       )
                                                  }
                                             >
                                                  <i
                                                       className={`fas ${
                                                            globalContext.isBulkSelectionEnabled.enabled ? "fa-times text-danger fs-2" : "fa-list-check text-dark fs-4"
                                                       }`}
                                                  />
                                             </button>
                                        </MyTooltip>
                                   )}

                                   <MyInputField
                                        name={"keyword"}
                                        value={helpers.values.keyword}
                                        isInvalid={false}
                                        placeholder={"Rechercher ..."}
                                        onChange={val => {
                                             helpers.setFieldValue("keyword", val).then()
                                             globalContext.filtersFormikRef.current?.setFieldValue("keyword", val)
                                        }}
                                        classNames={"border"}
                                   />
                                   <button type={"submit"} className={"btn btn-sm btn-outline btn-outline-secondary"} disabled={helpers.isSubmitting}>
                                        {helpers.isSubmitting ? <MySimpleSpinner size={"sm"} classNames={"text-dark"} /> : <i className={"fas fa-search text-dark"} />}
                                   </button>
                                   <MyTooltip title={"Réinitialiser les filtres"}>
                                        <button
                                             type={"button"}
                                             className={`btn btn-sm btn-outline btn-outline-secondary ${activeFilterCount > 0 ? "text-primary" : ""}`}
                                             onClick={() => handleReset()}
                                             disabled={helpers.isSubmitting}
                                        >
                                             <i className={"fas fa-sync text-dark"} />
                                        </button>
                                   </MyTooltip>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default ConsultantsListingSearchBar
